/**
 * Makes a whole card open its page, not only its "View Details" button.
 *
 * Cards marked `data-card` (the centre, programme, treatment and holistic
 * healing cards, written out in many page files) hold one link to their page
 * among other controls (Get Quote, Read More). A click anywhere else on the
 * card — its photo, its title, its text — follows that link, the same way
 * the button does (a new tab where the button opens one; Ctrl/⌘-click too).
 * A card whose "View Details" is a button that navigates in code (no link to
 * follow) has that button clicked instead.
 * Buttons, links and form fields inside the card keep their own behaviour,
 * and a click that ends a text selection selects, as before.
 *
 * The listener runs in the capture phase, ahead of React, so a title that
 * used to expand on click now opens the page instead (Read More still
 * expands).
 */
const CONTROLS = "a, button, input, select, textarea, label, summary, [role=button], [role=dialog]";

// A card whose page opens from a button (it navigates in code, no link to
// follow) uses that button instead: the one labelled like this.
const OPEN_BUTTON = /^(view details|view program|view package|view centre|view center|explore|read (full )?story|learn more)\b/i;

export function startCardLinks() {
  document.addEventListener(
    "click",
    (e) => {
      if (e.button !== 0 || e.defaultPrevented) return;
      const t = e.target as Element | null;
      const card = t?.closest?.("[data-card]");
      if (!card || t!.closest(CONTROLS)) return;
      const sel = window.getSelection();
      if (sel && !sel.isCollapsed && card.contains(sel.anchorNode)) return;
      const link = [...card.querySelectorAll<HTMLAnchorElement>("a[href]")].find((a) =>
        /^\/(?!\/)/.test(a.getAttribute("href") ?? "")
      );
      if (!link) {
        const button = [...card.querySelectorAll<HTMLButtonElement>("button")].find((b) =>
          OPEN_BUTTON.test((b.textContent ?? "").replace(/\s+/g, " ").trim())
        );
        if (!button) return;
        e.preventDefault();
        e.stopPropagation();
        button.click();
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      if (e.ctrlKey || e.metaKey || e.shiftKey) window.open(link.href, "_blank", "noopener");
      else link.click();
    },
    true
  );
}
